import React, { useState, useEffect } from 'react';
import { FaArrowRight } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import { fetchAkademikFaaliyet } from '../../utils/akademikFaaliyetCache';

const CURRENT_YEAR = new Date().getFullYear();

const StatItem = ({ num, suffix, label, index }) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 10 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: 0.9 + index * 0.08, duration: 0.5, ease: 'easeOut' }}
      className="flex-1 text-center py-4 md:py-5 px-1"
    >
      <p className="text-lg md:text-3xl font-black text-white leading-none tabular-nums">
        {inView ? <CountUp end={num} duration={2.2} separator="." /> : '0'}{suffix}
      </p>
      <p className="text-[8px] md:text-[10px] text-white/45 uppercase tracking-wider mt-1.5 leading-none">{label}</p>
    </motion.div>
  );
};

const HeroSection = () => {
  const { t } = useTranslation();
  const titleWords = t('hero.facultyName').split(' ');
  const [yayinSayisi, setYayinSayisi] = useState(null);

  useEffect(() => {
    fetchAkademikFaaliyet()
      .then(data => setYayinSayisi(data.makaleler.length))
      .catch(() => {});
  }, []);

  const BASE_STATS = [
    { num: 2627,  suffix: '+', label: t('home.stats.undergraduateStudents') },
    { num: 511,   suffix: '+', label: t('home.stats.graduateStudents') },
    { num: 183,   suffix: '',  label: t('home.stats.facultyMembers') },
    { num: 103,   suffix: '+', label: t('home.stats.activeProjects') },
    { num: 15824, suffix: '+', label: t('home.stats.totalAlumni') },
  ];

  const stats = [
    ...BASE_STATS,
    { num: yayinSayisi ?? 0, suffix: '', label: `${t('home.stats.publicationsYear')} ${CURRENT_YEAR}`, loading: yayinSayisi === null },
  ];

  return (
    <section className="relative h-screen overflow-hidden bg-black">
      <div className="absolute inset-0 w-full h-full">
        <video
          autoPlay muted loop playsInline
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: 'center center' }}
          onError={e => {
            if (e.target) e.target.style.display = 'none';
            const fb = document.getElementById('video-fallback');
            if (fb) fb.style.display = 'block';
          }}
        >
          <source src="/fenfakultesivideo.mp4" type="video/mp4" />
        </video>
        <div id="video-fallback" className="absolute inset-0 bg-gradient-to-br from-gray-900 to-gray-800 hidden" />
        <div className="absolute inset-0 bg-black/55 pointer-events-none" />
      </div>

      {/* ── Dekoratif çizgi — sol üst ── */}
      <motion.div
        className="absolute left-0 top-0 bottom-0 w-[2px] z-10"
        style={{ background: 'linear-gradient(to bottom, transparent, rgba(123,13,30,0.6) 40%, rgba(123,13,30,0.3) 70%, transparent)' }}
        initial={{ scaleY: 0, opacity: 0 }}
        animate={{ scaleY: 1, opacity: 1 }}
        transition={{ duration: 1.4, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
      />

      {/* ── Ana İçerik — sol hizalı ── */}
      <div className="relative z-10 h-full flex flex-col justify-center px-8 md:px-20 lg:px-28 pb-4 pt-16">

        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, x: -24, filter: 'blur(6px)' }}
          animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mb-7"
        >
          <span className="inline-flex items-center gap-2 text-white/55 text-xs font-semibold tracking-[0.22em] uppercase border border-white/15 rounded-full px-4 py-2 backdrop-blur-sm bg-white/5">
            {t('hero.universityName')}
          </span>
        </motion.div>

        {/* Başlık — kelime kelime clip reveal */}
        <h1
          className="font-black text-white uppercase leading-[0.88] tracking-tight mb-3"
          style={{ fontSize: 'clamp(3.2rem, 8.5vw, 6.5rem)' }}
        >
          {titleWords.map((word, i) => (
            <span
              key={i}
              style={{ display: 'inline-block', overflow: 'hidden', marginRight: i < titleWords.length - 1 ? '0.28em' : 0 }}
            >
              <motion.span
                style={{ display: 'block' }}
                initial={{ y: '108%', opacity: 0.6 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 1, delay: 0.22 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                {word}
              </motion.span>
            </span>
          ))}
        </h1>

        {/* Kırmızı aksan çizgisi */}
        <motion.div
          className="mt-5 mb-6 h-[1.5px] rounded-full"
          style={{ background: 'linear-gradient(to right, #7B0D1E, rgba(255,255,255,0.15), transparent)', transformOrigin: 'left' }}
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ scaleX: 1, opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
        />

        {/* Tanıtım metni */}
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.78, ease: [0.22, 1, 0.36, 1] }}
          className="text-white/55 text-sm md:text-[15px] leading-relaxed mb-8 max-w-lg"
          style={{ fontWeight: 300, letterSpacing: '0.01em' }}
        >
          {t('hero.tagline')}
        </motion.p>

        {/* Buton */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center gap-5"
        >
          <motion.button
            onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
            className="inline-flex items-center gap-2.5 bg-white text-gray-900 font-bold text-sm px-6 py-3 rounded-full shadow-lg shadow-black/30"
            whileHover={{ scale: 1.04, backgroundColor: '#f0f0f0' }}
            whileTap={{ scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
          >
            {t('hero.discoverFaculty')}
            <motion.span
              animate={{ x: [0, 3, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }}
            >
              <FaArrowRight className="w-3 h-3" />
            </motion.span>
          </motion.button>
        </motion.div>

      </div>

      {/* ── İstatistik Barı ── */}
      <div className="absolute bottom-0 left-0 right-0 z-20 bg-black/40 backdrop-blur-md border-t border-white/10">
        <div className="max-w-5xl mx-auto flex divide-x divide-white/10">
          {stats.map((s, i) => (
            s.loading
              ? <div key={i} className="flex-1 text-center py-4 md:py-5 px-1 flex flex-col items-center justify-center gap-1.5">
                  <div className="w-10 h-5 bg-white/10 rounded animate-pulse mx-auto" />
                  <p className="text-[8px] md:text-[10px] text-white/45 uppercase tracking-wider mt-0.5">{s.label}</p>
                </div>
              : <StatItem key={i} {...s} index={i} />
          ))}
        </div>
      </div>

    </section>
  );
};

export default HeroSection;
